import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { orderCancelReasonApi } from '@app/store';
import { IOrderCancelReason, OrderCancelReasonDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { OrderCancelReasonForm } from './OrderCancelReasonForm';
import { OrderCancelReasonFormValues } from './order-cancel-reason-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { OrderCancelReasonCard } from './OrderCancelReasonCard';
import { CheckPermissions } from '@app/components/CheckPermissions';

const transformToUpdatableFormOrderCancelReason = (orderCancelReason?: Partial<IOrderCancelReason>): OrderCancelReasonFormValues => {
  return {
    reason: orderCancelReason?.reason,
  }
}

interface OrderCancelReasonUpdateProps {
  orderCancelReason?: Partial<IOrderCancelReason>;
  isFetching?: boolean;
  toggle: (mode: CardButtonActionMode) => void;
  onDelete: (product: Partial<IOrderCancelReason>) => void;
}

export const OrderCancelReasonUpdate = ({ orderCancelReason, isFetching, toggle, onDelete }: OrderCancelReasonUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = orderCancelReasonApi.useUpdateOneOrderCancelReasonsMutation();

  const formMethods = useForm<OrderCancelReasonFormValues>({
    resolver: classValidatorResolver(OrderCancelReasonDTO),
    defaultValues: transformToUpdatableFormOrderCancelReason(orderCancelReason),
  });

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormOrderCancelReason(orderCancelReason));
  }, [orderCancelReason, formMethods.reset]);

  const onUpdate = async (formValues: OrderCancelReasonFormValues) => {
    try {
      const updatedOrderCancelReason = await update({ _id: orderCancelReason?._id, ...formValues }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedOrderCancelReason });

      toast.success('Modification effectuée avec succès.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification.')
    }
  }

  return (
    <Fragment>
      <OrderCancelReasonCard
        orderCancelReason={orderCancelReason}
        formMethods={formMethods}
        toggle={toggle} />

      <Panel.Content>

        <Form isLoading={isFetching} formMethods={formMethods} apiErrors={apiErrors}>

          <Panel.FormContainer>

            <CheckPermissions permissions={['dashboard::order-cancel-reasons.delete']}>
              <Panel.Header>
                <Button
                  title="Supprimer"
                  onPress={() => onDelete(orderCancelReason!)}
                  bordered
                  important />
              </Panel.Header>
            </CheckPermissions>

            <OrderCancelReasonForm />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>

        </Form>

      </Panel.Content>
    </Fragment>
  );
};
